/* eslint-disable no-script-url */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React from "react";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import UsersList from "../../Modals/usersList";
import { useNavigate } from "react-router-dom";

import { useDeleteAdminNotification, useGetAdminNotifications } from "../../api/hooks/useNotification";
import { NOTIFICATION_TYPE } from "../../api/types/notification.types";
import { CircularProgress } from "@mui/material";
import { showError, showSuccess } from "../../helpers/toast";
import ConfirmationDialog from "../../components/ConfirmationDialog";

const NotificationsList = ({
  searchTerm,
  typeFilter
}: {
  searchTerm?: string;
  typeFilter?: number | 'default';
}) => {
  const navigate = useNavigate();

  const { data: notificationsData, isLoading } = useGetAdminNotifications({
    page: 1,
    limit: 100,
    search: searchTerm,
    type: typeFilter === 'default' ? undefined : typeFilter as number
  });

  const notifications = notificationsData?.data?.list || [];
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [notificationToDelete, setNotificationToDelete] = React.useState<string | null>(null);
  const { mutate: deleteNotification, isPending: isDeleting } = useDeleteAdminNotification({
    onSuccess: (response) => {
      showSuccess(response?.message || "Notification deleted successfully");
      setDeleteDialogOpen(false);
      setNotificationToDelete(null);
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to delete notification");
    },
  });

  // This is still needed for the modal if we want to show details, 
  // but for now we'll use empty or partial data since we don't have the full user objects here
  const usersList: any[] = [];

  const getTypeLabel = (type: number) => {
    if (type === NOTIFICATION_TYPE.BROADCAST) return "Broadcast";
    if (type === NOTIFICATION_TYPE.MAIL) return "Email";
    return "Unknown";
  };

  const getUserLabel = (isSendToAll: boolean, userIds: string[]) => {
    if (isSendToAll) return "All Users";
    return `${userIds?.length || 0} Users`;
  };

  const [openUserModal, setOpenUserModal] = React.useState(false);
  const [selectedUserIds, setSelectedUserIds] = React.useState<string[]>([]);

  const handleDeleteClick = (id: string) => {
    setNotificationToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (notificationToDelete) {
      deleteNotification(notificationToDelete);
    }
  };

  const handleDeleteCancel = () => {
    if (!isDeleting) {
      setDeleteDialogOpen(false);
      setNotificationToDelete(null);
    }
  };

  return (
    <>
      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Audience</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : notifications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  No notifications found.
                </TableCell>
              </TableRow>
            ) : (
              notifications.map((row: any) => (
                <TableRow hover key={row._id}>
                  <TableCell>{row.title}</TableCell>
                  <TableCell>{getTypeLabel(row.type)}</TableCell>
                  <TableCell
                    className="click_action"
                    onClick={() => {
                      setSelectedUserIds(row.broadcastReceivers || []);
                      setOpenUserModal(true);
                    }}
                  >
                    {getUserLabel(row.isSendToAll, row.broadcastReceivers)}
                  </TableCell>
                  <TableCell>
                    {row.createdAt
                      ? new Date(row.createdAt).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      }).replace(/ /g, "-")
                      : "--"}
                  </TableCell>
                  <TableCell>
                    <Box className="table_actions v2">
                      <IconButton onClick={() => navigate("/manage-notifications/detail", { state: { id: row._id } })}>
                        <img src="/static/images/eye_icon.svg" alt="View" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteClick(row._id)}
                        disabled={isDeleting}
                      >
                        <img src="/static/images/trash2_icon.svg" alt="Delete" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <UsersList
        open={openUserModal}
        onClose={() => setOpenUserModal(false)}
        setOpen={setOpenUserModal}
        userIds={selectedUserIds}
        usersList={usersList}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Notification"
        message="Are you sure you want to delete this notification? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="error"
        isLoading={isDeleting}
        loadingText="Deleting..."
      />
    </>
  );
};

export default NotificationsList;
